(function () {
  /* -----------------------------
   * Utils
   * ----------------------------- */
  function getAllParams(name, search) {
    const params = new URLSearchParams(search || window.location.search);
    return params.getAll(name).filter(Boolean);
  }

  function normalize(str) {
    return (str || '').trim();
  }

  function px(val, fallback) {
    const n = parseFloat(val);
    return Number.isFinite(n) ? n : fallback;
  }

  function getRowGapPx(el) {
    const cs = getComputedStyle(el);
    const rg = cs.rowGap && cs.rowGap !== 'normal' ? cs.rowGap : '';
    const gp = cs.gap && cs.gap !== 'normal' ? cs.gap : '';
    const v = rg || (gp ? gp.split(' ')[0] : '');
    return px(v, 10);
  }

  /* -----------------------------
   * Global scheduler (one rAF)
   * ----------------------------- */
  const ROWLINE_CONTAINERS = new Set();
  let rafScheduled = false;

  function scheduleAll() {
    if (rafScheduled) return;
    rafScheduled = true;
    requestAnimationFrame(function () {
      rafScheduled = false;
      ROWLINE_CONTAINERS.forEach((c) => {
        if (c && c._psRowLinesCalcNow) c._psRowLinesCalcNow();
      });
    });
  }

  /* -----------------------------
   * Row divider engine (for any flex-wrap list)
   * - rect-based
   * - row-gap reflected
   * - no per-container window listeners
   * ----------------------------- */
  function initRowLines(container) {
    if (!container || container.dataset.psRowLinesInited === '1') return;
    container.dataset.psRowLinesInited = '1';

    // ✅ lines layer inside container
    let linesLayer = container.querySelector(':scope > .ps-ch__row-lines');
    if (!linesLayer) {
      linesLayer = document.createElement('div');
      linesLayer.className = 'ps-ch__row-lines no-empty';
      container.appendChild(linesLayer);
    } else {
      linesLayer.classList.add('no-empty');
    }

    // register globally
    ROWLINE_CONTAINERS.add(container);

    let ro = null;

    function getVisibleItems() {
      return Array.from(container.children).filter((el) => {
        if (!(el instanceof HTMLElement)) return false;
        if (el === linesLayer) return false;
        if (el.matches('[hidden], .is-suppressed')) return false;
        if (el.offsetParent === null) return false;
        return true;
      });
    }

    function ensureLineEls(count) {
      const current = linesLayer.children.length;
      for (let i = current; i < count; i++) {
        const d = document.createElement('div');
        d.className = 'ps-ch__row-line no-empty';
        linesLayer.appendChild(d);
      }
      for (let i = current - 1; i >= count; i--) {
        linesLayer.removeChild(linesLayer.lastElementChild);
      }
    }

    function calcNow() {
      const items = getVisibleItems();
      if (!items.length) {
        ensureLineEls(0);
        return;
      }

      const cRect = container.getBoundingClientRect();
      const rowGap = getRowGapPx(container);

      const measured = items
        .map((el) => {
          const r = el.getBoundingClientRect();
          return { top: r.top - cRect.top, bottom: r.bottom - cRect.top };
        })
        .sort((a, b) => a.top - b.top);

      const ROW_TOL = 6;
      const rows = [];
      for (const it of measured) {
        const last = rows[rows.length - 1];
        if (!last || Math.abs(it.top - last.top) > ROW_TOL) {
          rows.push({ top: it.top, bottom: it.bottom });
        } else {
          last.bottom = Math.max(last.bottom, it.bottom);
        }
      }

      const rawLineCount = Math.max(0, rows.length - 1);
      ensureLineEls(rawLineCount);
      if (!rawLineCount) return;

      const SAFE_PAD = 6;
      const EDGE_SKIP = 8;
      const H = container.clientHeight;

      let idx = 0;
      for (let i = 1; i < rows.length; i++) {
        const prev = rows[i - 1];
        const next = rows[i];

        // row-gap 중심 목표
        let y = prev.bottom + rowGap / 2;
        const maxY = next.top - rowGap / 2;
        if (Number.isFinite(maxY)) y = Math.min(y, maxY);

        // clamp
        if (y < prev.bottom + SAFE_PAD) y = prev.bottom + SAFE_PAD;
        if (y > next.top - SAFE_PAD) y = next.top - SAFE_PAD;

        // edge skip
        if (y <= EDGE_SKIP || y >= H - EDGE_SKIP) continue;

        const line = linesLayer.children[idx];
        if (!line) break;
        line.style.top = y + 'px';
        idx++;
      }

      ensureLineEls(idx);
    }

    container._psRowLinesCalcNow = calcNow;

    // observers: only ResizeObserver per container
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(scheduleAll);
      ro.observe(container);
    }

    // first paint (immediate + next frame)
    calcNow();
    scheduleAll();
  }

  function initAllRowLines(root) {
    if (!root) return;

    const inline = root.querySelector('.ps-ch__types');
    if (inline) initRowLines(inline);

    const panelList = root.querySelector(
      '[data-ps-more-panel] [data-ps-panel-list], [data-ps-more-panel] .ps-ch__panel-list'
    );
    if (panelList) initRowLines(panelList);
  }

  function calcAllRowLinesNow(root) {
    if (!root) return;

    const inline = root.querySelector('.ps-ch__types');
    if (inline && inline._psRowLinesCalcNow) inline._psRowLinesCalcNow();

    const panelList = root.querySelector(
      '[data-ps-more-panel] [data-ps-panel-list], [data-ps-more-panel] .ps-ch__panel-list'
    );
    if (panelList && panelList._psRowLinesCalcNow) panelList._psRowLinesCalcNow();
  }

  /* -----------------------------
   * More count / Active sync
   * ----------------------------- */
  function updateMoreCount(root, useVisibleCount) {
    const trigger = root.querySelector('[data-ps-more-trigger]');
    const panel = root.querySelector('[data-ps-more-panel]');
    if (!trigger || !panel) return;

    const nameEl = trigger.querySelector('.ps-ch__type-name');
    if (!nameEl) return;

    const items = Array.from(panel.querySelectorAll('.ps-ch__type[data-ps-type]'));
    const totalCount = items.length;

    if (totalCount === 0) {
      trigger.remove();
      panel.remove();
      return;
    }

    if (!trigger.dataset.psMoreBaseLabel) {
      trigger.dataset.psMoreBaseLabel = normalize(nameEl.textContent) || 'More';
    }
    const baseLabel = trigger.dataset.psMoreBaseLabel;

    const visibleCount = items.filter((a) => a.style.display !== 'none').length;
    const count = useVisibleCount ? visibleCount : totalCount;

    nameEl.textContent = `${count} ${baseLabel}`;
  }

  function syncActive(root) {
    const param = root.getAttribute('data-ps-filter-param') || 'filter.p.product_type';
    const activeList = getAllParams(param).map(normalize);
    const hasActive = activeList.length > 0;
    const activeType = hasActive ? activeList[0] : '';

    const allButtons = Array.from(root.querySelectorAll('.ps-ch__type[data-ps-type]'));
    if (!allButtons.length) return;

    allButtons.forEach((btn) => {
      btn.classList.remove('is-active');
      btn.setAttribute('aria-current', 'false');
    });

    let hasInlineActive = false;
    allButtons.forEach((btn) => {
      const value = normalize(btn.getAttribute('data-ps-type') || '');

      if (!hasActive && value === '') {
        btn.classList.add('is-active');
        btn.setAttribute('aria-current', 'true');
        return;
      }

      if (hasActive && value !== '' && value === activeType) {
        btn.classList.add('is-active');
        btn.setAttribute('aria-current', 'true');
        if (!btn.classList.contains('ps-ch__panel-item')) hasInlineActive = true;
      }
    });

    const moreTrigger = root.querySelector('[data-ps-more-trigger]');
    if (moreTrigger) {
      if (hasActive && !hasInlineActive) moreTrigger.classList.add('is-active');
      else moreTrigger.classList.remove('is-active');
    }

    updateMoreCount(root, false);
    scheduleAll();
  }

  /* -----------------------------
   * Panel open/close
   * ----------------------------- */
  function closePanel(root) {
    const panel = root.querySelector('[data-ps-more-panel]');
    const trigger = root.querySelector('[data-ps-more-trigger]');
    if (!panel || !trigger) return;

    panel.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');

    const input = panel.querySelector('[data-ps-type-search]');
    if (input) {
      input.value = '';
      const items = panel.querySelectorAll('.ps-ch__type[data-ps-type]');
      items.forEach((a) => (a.style.display = ''));
      updateMoreCount(root, false);
    }

    scheduleAll();
  }

  function openPanel(root) {
    const panel = root.querySelector('[data-ps-more-panel]');
    const trigger = root.querySelector('[data-ps-more-trigger]');
    if (!panel || !trigger) return;

    const items = panel.querySelectorAll('.ps-ch__type[data-ps-type]');
    if (!items || items.length === 0) {
      trigger.hidden = true;
      panel.hidden = true;
      return;
    }

    panel.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    updateMoreCount(root, false);

    // panel appears -> calc immediately + next frame
    calcAllRowLinesNow(root);
    scheduleAll();
  }

  function togglePanel(root) {
    const panel = root.querySelector('[data-ps-more-panel]');
    if (!panel) return;
    if (panel.hidden) openPanel(root);
    else closePanel(root);
  }

  /* -----------------------------
   * Wire (dedup)
   * ----------------------------- */
  function wire(root) {
    if (!root) return;

    initAllRowLines(root);

    if (root.dataset.psChInited === '1') {
      syncActive(root);
      return;
    }
    root.dataset.psChInited = '1';

    syncActive(root);

    const trigger = root.querySelector('[data-ps-more-trigger]');
    const closeBtn = root.querySelector('[data-ps-more-close]');
    const panel = root.querySelector('[data-ps-more-panel]');

    if (trigger) {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        if (trigger.hidden) return;
        togglePanel(root);
      });
    }

    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        closePanel(root);
      });
    }

    const input = root.querySelector('[data-ps-type-search]');
    if (input) {
      input.addEventListener('input', () => {
        const q = normalize(input.value).toLowerCase();
        const items = root.querySelectorAll('[data-ps-more-panel] .ps-ch__type[data-ps-type]');
        items.forEach((a) => {
          const t = normalize(a.getAttribute('data-ps-type') || '').toLowerCase();
          a.style.display = (!q || t.includes(q)) ? '' : 'none';
        });
        updateMoreCount(root, true);
        scheduleAll();
      });
    }

    if (panel) {
      panel.addEventListener('click', (e) => {
        const a = e.target.closest('a.ps-ch__type[data-ps-type]');
        if (!a) return;
        closePanel(root);
      });
    }
  }

  /* -----------------------------
   * wireAll (microtask-debounced)
   * ----------------------------- */
  let wireQueued = false;
  function wireAll() {
    if (wireQueued) return;
    wireQueued = true;
    Promise.resolve().then(function () {
      wireQueued = false;
      document.querySelectorAll('[data-ps-collection-header]').forEach(wire);
      scheduleAll();
    });
  }

  document.addEventListener('DOMContentLoaded', wireAll);
  document.addEventListener('collection:rerendered', wireAll);
  window.addEventListener('popstate', wireAll);

  // global resize once
  window.addEventListener('resize', scheduleAll, { passive: true });

  // fonts ready once
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(scheduleAll).catch(function () {});
  }

  const _pushState = history.pushState;
  const _replaceState = history.replaceState;

  history.pushState = function () {
    _pushState.apply(this, arguments);
    wireAll();
  };

  history.replaceState = function () {
    _replaceState.apply(this, arguments);
    wireAll();
  };
})();